import React, { useState, useEffect, useRef, useCallback } from 'react';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Zap from 'lucide-react/dist/esm/icons/zap';
import Download from 'lucide-react/dist/esm/icons/download';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import AlertOctagon from 'lucide-react/dist/esm/icons/alert-octagon';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import FinancialCharts from './FinancialCharts';
import ImproveAnalysisModal from './ImproveAnalysisModal';
import { getTranslation } from '../../../i18n';
import { getCachedLocalStorage } from '../../../utils/storage';


const ResultsDisplay = ({ data, language, onLanguageChange, jobId, analysisId, onToggleChat, chatOpen, chatMessageCount = 0, onExtendAnalysis }) => {
  const t = useCallback((key) => getTranslation(language, key), [language]);
  const [exporting, setExporting] = useState(false);
  const [exportingProfessional, setExportingProfessional] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const actionBarRef = useRef(null);
  
  // Memoize scroll handler
  const handleScroll = useCallback(() => {
    const mainContent = document.querySelector('main');
    if (!mainContent || !actionBarRef.current) return;
    
    const rect = actionBarRef.current.getBoundingClientRect();
    const threshold = 60; // Height of header
    
    setShowStickyBar(rect.top < threshold);
    setShowScrollTop(mainContent.scrollTop > 400);
  }, []);
  
  // Track scroll position for sticky action bar
  useEffect(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll);
      return () => mainContent.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);
  
  const scrollToTop = useCallback(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  
  const handleExtendAnalysis = useCallback(async (id, files, guidance) => {
    if (onExtendAnalysis) {
      setIsExtending(true);
      try {
        await onExtendAnalysis(id, files, guidance);
        setShowImproveModal(false);
      } catch (error) {
        console.error('Error extending analysis:', error);
      } finally {
        setIsExtending(false);
      }
    }
  }, [onExtendAnalysis]);
  
  const handleExportPDF = useCallback(async () => {
    if (!jobId) return;
    setExporting(true);
    try {
      const token = getCachedLocalStorage('quazi_access_token');
      const response = await fetch(`/api/export-pdf/${jobId}?language=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to export PDF');
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'QUAZI_Report.pdf';
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert(t('exportError') || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  }, [jobId, language, t]);
  
  const handleExportProfessionalPDF = useCallback(async () => {
    if (!jobId) return;
    setExportingProfessional(true);
    try {
      const token = getCachedLocalStorage('quazi_access_token');
      const response = await fetch(`/api/export-professional-pdf/${jobId}?language=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to export professional PDF');
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'QUAZI_Management_Report.pdf';
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Professional export error:', error);
      alert(t('exportError') || 'Failed to export professional PDF');
    } finally {
      setExportingProfessional(false);
    }
  }, [jobId, language, t]);
  
  const formatLabel = (text) => {
    if (!text) return '';
    return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const generateActionContext = (actionText, priority, deadlineDays) => {
    const text = actionText?.toLowerCase() || '';
    if (text.includes('restructuring') && text.includes('logistics')) return 'Analyze division performance metrics, identify root causes of underperformance, and develop a comprehensive turnaround strategy.';
    if (text.includes('operational efficiency')) return 'Implement process improvements, eliminate redundancies, and standardize best practices across all divisions.';
    if (text.includes('cost') && text.includes('reduction')) return 'Focus on operational efficiency improvements, negotiate better vendor contracts, and eliminate non-essential expenses.';
    if (text.includes('profitability') || text.includes('profit')) return 'Analyze revenue streams and cost structures to identify key profit drivers.';
    if (text.includes('debt') || text.includes('liquidity')) return 'Develop a comprehensive debt management strategy and explore refinancing options.';
    if (text.includes('cash flow') || text.includes('working capital')) return 'Accelerate receivables collection, optimize payment terms, and improve inventory management.';
    if (text.includes('revenue')) return 'Diversify revenue sources and strengthen customer relationships.';
    return `Address this ${priority?.toLowerCase() || 'priority'} item systematically through detailed planning and regular progress monitoring.`;
  };

  const getSeverityClass = (severity) => {
    const base = 'text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider';
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return `${base} bg-status-error/20 text-status-error border border-status-error/30`;
      case 'HIGH': return `${base} bg-orange-500/20 text-orange-400 border border-orange-500/30`;
      case 'MEDIUM': return `${base} bg-yellow-500/20 text-yellow-400 border border-yellow-500/30`;
      default: return `${base} bg-surface-elevated text-content-secondary border border-strong`;
    }
  };

  const getCardBorder = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'border-l-red-500';
      case 'HIGH': return 'border-l-orange-500';
      case 'MEDIUM': return 'border-l-yellow-500';
      default: return 'border-l-white/20';
    }
  };

  return (
    <div className="p-8 animate-fade-in relative">
      {/* Sticky Action Bar */}
      <div 
        className={`fixed top-[60px] left-0 right-0 z-sticky transition-all duration-300 ${
          showStickyBar 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
      >
        <div className="bg-surface-page/95 backdrop-blur-xl border-b border-default py-3 px-6">
          <div className="max-w-[1400px] mx-auto flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-content truncate max-w-[200px]">{data.company_info?.name}</span>
              <div className="h-4 w-px bg-surface-elevated" />
              <div className="flex gap-2">
                <button 
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 bg-gradient-to-br from-brand to-brand-hover border-none rounded-lg text-content-inverse text-xs font-semibold cursor-pointer transition-all duration-200 hover:scale-105 disabled:opacity-50"
                  onClick={handleExportPDF}
                  disabled={exporting || exportingProfessional || !jobId}
                >
                  <FileText size={14} className={exporting ? 'animate-spin' : ''} />
                  {exporting ? 'Exporting...' : 'Export PDF'}
                </button>
                <button 
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 bg-gradient-to-br from-brand to-brand-hover border-none rounded-lg text-content-inverse text-xs font-semibold cursor-pointer transition-all duration-200 hover:scale-105 disabled:opacity-50"
                  onClick={handleExportProfessionalPDF}
                  disabled={exporting || exportingProfessional || !jobId}
                >
                  <Download size={14} className={exportingProfessional ? 'animate-spin' : ''} />
                  Report
                </button>
              </div>
            </div>
<div className="flex gap-2">
                <button 
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-medium cursor-pointer transition-all duration-200 border bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border-violet-500/30 text-violet-400 hover:from-violet-500/30 hover:to-cyan-500/30"
                  onClick={() => setShowImproveModal(true)}
                >
                  <Sparkles size={14} />
                  Improve
                </button>
                {data.data_extraction && (
                  <button 
                    className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-medium cursor-pointer transition-all duration-200 border ${
                      chartsOpen ? 'bg-status-success/20 border-status-success/50 text-status-success' : 'bg-surface-primary border-default text-content-secondary hover:bg-surface-elevated'
                    }`}
                    onClick={() => setChartsOpen(!chartsOpen)}
                  >
                    <BarChart3 size={14} />
                    Charts
                  </button>
                )}
                {onToggleChat && (
                  <button 
                    className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-medium cursor-pointer transition-all duration-200 border ${
                      chatOpen ? 'bg-status-success/20 border-status-success/50 text-status-success' : 'bg-surface-primary border-default text-content-secondary hover:bg-surface-elevated'
                    }`}
                    onClick={onToggleChat}
                  >
                    <MessageSquare size={14} />
                    Chat
                    {chatMessageCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 bg-gradient-to-br from-brand to-brand-hover rounded-full text-[10px] font-bold text-content-inverse">
                        {chatMessageCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        className={`fixed bottom-20 right-6 z-sticky w-10 h-10 rounded-full bg-brand/90 text-content flex items-center justify-center cursor-pointer shadow-lg transition-all duration-300 hover:bg-brand hover:scale-110 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        onClick={scrollToTop}
        title="Scroll to top"
      >
        <ChevronUp size={20} />
      </button>

      {/* Company Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-br from-brand/10 via-surface-panel-from/90 to-surface-panel-to/95 border border-brand/30 overflow-hidden">
        {/* Action Bar */}
        <div ref={actionBarRef} className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex flex-wrap gap-3" data-walkthrough="export">
            <button 
              className="inline-flex items-center gap-2 py-3 px-5 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-sm font-semibold cursor-pointer shadow-glow transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow-strong disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportPDF}
              disabled={exporting || exportingProfessional || !jobId}
            >
              <FileText size={20} className={exporting ? 'animate-spin' : ''} />
              {exporting ? t('exportingPDF') : t('exportPDF')}
            </button>
            
            <button 
              className="inline-flex items-center gap-2 py-3 px-5 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-sm font-semibold cursor-pointer shadow-[0_0_20px_rgba(139,92,246,0.4)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportProfessionalPDF}
              disabled={exporting || exportingProfessional || !jobId}
            >
              <Download size={20} className={exportingProfessional ? 'animate-spin' : ''} />
              {exportingProfessional ? t('exportingProfessionalPDF') : t('exportProfessionalPDF')}
            </button>
          </div>
          
          <div className="flex gap-3">
            <button 
              className="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/30 text-violet-400 hover:from-violet-500/30 hover:to-cyan-500/30"
              onClick={() => setShowImproveModal(true)}
              data-walkthrough="improve"
            >
              <Sparkles size={18} />
              <span>{t('improveAnalysis') || 'Improve Analysis'}</span>
            </button>
            
            {data.data_extraction && (
              <button 
                className={`inline-flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 ${
                  chartsOpen 
                    ? 'bg-status-success/20 border-status-success/50 text-status-success' 
                    : 'bg-surface-primary border-default text-content-secondary hover:bg-surface-elevated hover:border-strong'
                } border`}
                onClick={() => setChartsOpen(!chartsOpen)}
                data-walkthrough="charts"
              >
                <BarChart3 size={18} />
                <span>{t('viewCharts') || 'Charts'}</span>
              </button>
            )}
            
            {onToggleChat && (
              <button 
                className={`inline-flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 ${
                  chatOpen 
                    ? 'bg-status-success/20 border-status-success/50 text-status-success' 
                    : 'bg-surface-primary border-default text-content-secondary hover:bg-surface-elevated hover:border-strong'
                } border`}
                onClick={onToggleChat}
                data-walkthrough="chat"
              >
                <MessageSquare size={18} />
                <span>{t('chatWithQuazi') || 'Chat with QUAZI'}</span>
                {chatMessageCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gradient-to-br from-brand to-brand-hover rounded-full text-xs font-bold text-content-inverse">
                    {chatMessageCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Company Name */}
        <h1 className="text-4xl font-bold text-content mb-6 tracking-tight">{data.company_info?.name}</h1>

        {/* Executive Summary */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
            {t('executiveSummary')}
          </h3>
          <p className="text-base text-content-secondary leading-relaxed">{data.executive_summary?.[language]}</p>
        </div>
      </div>

      {/* Financial Charts */}
      {data.data_extraction && (
        <FinancialCharts 
          dataExtraction={data.data_extraction}
          currency={data.company_info?.currency || 'EUR'}
          language={language}
          externalOpen={chartsOpen}
          onOpenChange={setChartsOpen}
        />
      )}

      {/* Green Flags */}
      {data.green_flags && data.green_flags.length > 0 && (
        <div className="mb-8">
          <h3 className="flex items-center gap-3 text-xl font-bold text-status-success mb-6">
            <CheckCircle size={24} />
            {t('positiveIndicators')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.green_flags.map((flag, index) => (
              <div key={index} className="p-6 rounded-2xl bg-status-success/5 border border-status-success/20 border-l-4 border-l-success glass">
                <div className="flex items-start gap-4 mb-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-status-success/20 text-status-success font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </span>
                  <h4 className="text-base font-semibold text-content leading-snug">{flag.title?.[language]}</h4>
                </div>
                <p className="text-sm text-content-secondary leading-relaxed ml-12">{flag.description?.[language]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Red Flags */}
      {data.red_flags && data.red_flags.length > 0 && (
        <div className="mb-8">
          <h3 className="flex items-center gap-3 text-xl font-bold text-status-error mb-6">
            <AlertTriangle size={24} />
            {t('criticalAlerts')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.red_flags.map((flag, index) => (
              <div key={index} className={`p-6 rounded-2xl bg-surface-primary border border-default border-l-4 ${getCardBorder(flag.severity)} glass`}>
                <span className={getSeverityClass(flag.severity)}>{t(flag.severity?.toLowerCase())}</span>
                <h4 className="text-base font-semibold text-content mt-4 mb-2">{flag.title?.[language]}</h4>
                <p className="text-sm text-content-secondary leading-relaxed">{flag.description?.[language]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anomalies */}
      {data.anomalies_detected && data.anomalies_detected.length > 0 && (
        <div className="mb-8">
          <h3 className="flex items-center gap-3 text-xl font-bold text-orange-500 mb-6">
            <AlertOctagon size={24} />
            {t('anomaliesDetected')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...data.anomalies_detected]
              .sort((a, b) => {
                const severityOrder = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
                return (severityOrder[a.severity] || 999) - (severityOrder[b.severity] || 999);
              })
              .map((anomaly, index) => (
                <div key={index} className={`p-6 rounded-2xl bg-surface-primary border border-default border-l-4 ${getCardBorder(anomaly.severity)} glass`}>
                  <span className={getSeverityClass(anomaly.severity)}>{t(anomaly.severity?.toLowerCase())}</span>
                  <h4 className="text-base font-semibold text-content mt-4 mb-2">{formatLabel(anomaly.type)}</h4>
                  <p className="text-sm text-content-secondary leading-relaxed">{anomaly.description?.[language]}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Action Items */}
      {data.action_items && data.action_items.length > 0 && (
        <div className="mb-8">
          <h3 className="flex items-center gap-3 text-xl font-bold text-status-success mb-6">
            <Zap size={24} />
            {t('requiredActions')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.action_items.map((item, index) => (
              <div key={index} className={`p-6 rounded-2xl bg-surface-primary border border-default border-l-4 ${getCardBorder(item.priority)} glass`}>
                <span className={getSeverityClass(item.priority)}>{t(item.priority?.toLowerCase())}</span>
                <h4 className="text-base font-semibold text-content mt-4 mb-2">{item.item?.[language]}</h4>
                <p className="text-sm text-content-secondary leading-relaxed">
                  {item.description?.[language] || generateActionContext(item.item?.[language], item.priority, item.deadline_days)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Improve Analysis Modal */}
      <ImproveAnalysisModal
        isOpen={showImproveModal}
        onClose={() => setShowImproveModal(false)}
        language={language}
        onExtendAnalysis={handleExtendAnalysis}
        analysisId={analysisId || jobId}
        isExtending={isExtending}
      />
    </div>
  );
};

export default ResultsDisplay;
